<script setup lang="ts">
/**
 * MermaidZoom Component
 *
 * Full-screen zoom viewer for Mermaid diagrams.
 * Features:
 * - Mouse wheel zoom
 * - Click & drag pan
 * - Keyboard controls (+/- zoom, arrows pan, Esc close, 0 reset)
 * - Auto-detects mermaid diagrams and makes them zoomable
 *
 * Ported from VitePress theme with improvements for PrimeVue compatibility.
 *
 * Usage:
 * 1. Add <MermaidZoom /> to your layout (only once, at root level)
 * 2. Any .mermaid container becomes click-to-zoom
 */
import { ref, nextTick, onMounted, onUnmounted } from 'vue'
import { sanitizeHtml } from '../../utils/sanitize'

const isOpen = ref(false)
const currentSvg = ref('')
const scale = ref(1)
const position = ref({ x: 0, y: 0 })
const isDragging = ref(false)
const dragStart = ref({ x: 0, y: 0 })
const container = ref<HTMLElement | null>(null)

const MIN_SCALE = 0.05
const MAX_SCALE = 5
const ZOOM_STEP = 0.1

const openZoom = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  const mermaidContainer = target.closest('.mermaid')
  if (!mermaidContainer) return

  const svg = mermaidContainer.querySelector('svg')
  if (!svg) return

  // Clone the SVG and ensure it has explicit dimensions
  const clonedSvg = svg.cloneNode(true) as SVGElement

  // Get the actual rendered dimensions
  const bbox = svg.getBBox ? svg.getBBox() : null
  const rect = svg.getBoundingClientRect()

  // Set explicit dimensions on the cloned SVG
  const width = bbox ? bbox.width + bbox.x * 2 : rect.width
  const height = bbox ? bbox.height + bbox.y * 2 : rect.height

  // Ensure minimum dimensions and set viewBox if not present
  if (!clonedSvg.getAttribute('viewBox') && width && height) {
    clonedSvg.setAttribute('viewBox', `0 0 ${width} ${height}`)
  }

  // Set explicit width/height for proper display
  clonedSvg.style.width = `${Math.max(width, 200)}px`
  clonedSvg.style.height = `${Math.max(height, 150)}px`
  clonedSvg.style.minWidth = '200px'
  clonedSvg.style.minHeight = '150px'

  currentSvg.value = clonedSvg.outerHTML
  isOpen.value = true
  scale.value = 1
  position.value = { x: 0, y: 0 }
  document.body.style.overflow = 'hidden'

  // Focus container for keyboard events
  nextTick(() => {
    container.value?.focus()
  })
}

const closeZoom = () => {
  isOpen.value = false
  document.body.style.overflow = ''
}

const zoomIn = () => {
  scale.value = Math.min(MAX_SCALE, scale.value + ZOOM_STEP)
}

const zoomOut = () => {
  scale.value = Math.max(MIN_SCALE, scale.value - ZOOM_STEP)
}

const resetZoom = () => {
  scale.value = 1
  position.value = { x: 0, y: 0 }
}

const fitToScreen = () => {
  scale.value = 0.8
  position.value = { x: 0, y: 0 }
}

const handleWheel = (e: WheelEvent) => {
  e.preventDefault()
  if (e.deltaY < 0) {
    zoomIn()
  } else {
    zoomOut()
  }
}

const handleMouseDown = (e: MouseEvent) => {
  if (e.button !== 0) return // Only left click
  isDragging.value = true
  dragStart.value = {
    x: e.clientX - position.value.x,
    y: e.clientY - position.value.y,
  }
}

const handleMouseMove = (e: MouseEvent) => {
  if (!isDragging.value) return
  position.value = {
    x: e.clientX - dragStart.value.x,
    y: e.clientY - dragStart.value.y,
  }
}

const handleMouseUp = () => {
  isDragging.value = false
}

const handleKeydown = (e: KeyboardEvent) => {
  if (!isOpen.value) return

  switch (e.key) {
    case 'Escape':
      closeZoom()
      break
    case '+':
    case '=':
      zoomIn()
      break
    case '-':
      zoomOut()
      break
    case '0':
      resetZoom()
      break
    case 'ArrowUp':
      position.value.y += 50
      break
    case 'ArrowDown':
      position.value.y -= 50
      break
    case 'ArrowLeft':
      position.value.x += 50
      break
    case 'ArrowRight':
      position.value.x -= 50
      break
  }
}

const handleMermaidClick = (e: MouseEvent) => {
  const target = e.target as HTMLElement
  if (target.closest('.mermaid')) {
    openZoom(e)
  }
}

onMounted(() => {
  document.addEventListener('click', handleMermaidClick)
})

onUnmounted(() => {
  document.removeEventListener('click', handleMermaidClick)
})
</script>

<template>
  <Teleport to="body">
    <Transition name="zoom">
      <div
        v-if="isOpen"
        ref="container"
        class="mermaid-zoom-overlay"
        tabindex="0"
        @keydown="handleKeydown"
        @wheel.prevent="handleWheel"
        @mousedown="handleMouseDown"
        @mousemove="handleMouseMove"
        @mouseup="handleMouseUp"
        @mouseleave="handleMouseUp"
      >
        <!-- Controls -->
        <div class="mermaid-zoom-controls">
          <button @click.stop="zoomOut" title="Zoom out (-)" class="zoom-btn">
            <i class="pi pi-search-minus" />
          </button>
          <span class="zoom-level">{{ Math.round(scale * 100) }}%</span>
          <button @click.stop="zoomIn" title="Zoom in (+)" class="zoom-btn">
            <i class="pi pi-search-plus" />
          </button>
          <button @click.stop="resetZoom" title="Reset zoom (0)" class="zoom-btn">
            <i class="pi pi-refresh" />
          </button>
          <button @click.stop="fitToScreen" title="Fit to screen" class="zoom-btn">
            <i class="pi pi-window-maximize" />
          </button>
          <div class="divider" />
          <button @click.stop="closeZoom" class="zoom-btn close-btn" title="Close (Esc)">
            <i class="pi pi-times" />
          </button>
        </div>

        <!-- Help text -->
        <div class="mermaid-zoom-help">
          Scroll to zoom &bull; Drag to pan &bull; Arrow keys to move &bull; Esc to close
        </div>

        <!-- Diagram container -->
        <div
          class="mermaid-zoom-content"
          :class="{ dragging: isDragging }"
          :style="{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
          }"
          v-html="sanitizeHtml(currentSvg)"
        />
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.mermaid-zoom-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: theme('colors.secondary.900');
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  outline: none;
  overflow: hidden;
}

.mermaid-zoom-overlay:active {
  cursor: grabbing;
}

.mermaid-zoom-controls {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  background: theme('colors.secondary.800');
  padding: 8px 16px;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  z-index: 10001;
}

.zoom-btn {
  background: transparent;
  border: none;
  color: theme('colors.secondary.400');
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  font-size: 1rem;
}

.zoom-btn:hover {
  background: theme('colors.secondary.700');
  color: theme('colors.secondary.100');
}

.close-btn:hover {
  background: rgba(239, 68, 68, 0.2);
  color: theme('colors.danger.500');
}

.divider {
  width: 1px;
  height: 24px;
  background: theme('colors.secondary.700');
  margin: 0 8px;
}

.zoom-level {
  font-size: 0.875rem;
  font-weight: 600;
  min-width: 50px;
  text-align: center;
  color: theme('colors.secondary.400');
  font-family: 'JetBrains Mono', monospace;
}

.mermaid-zoom-help {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  background: rgba(0, 0, 0, 0.5);
  padding: 8px 16px;
  border-radius: 6px;
  z-index: 10001;
}

.mermaid-zoom-content {
  transform-origin: center center;
  transition: transform 0.1s ease-out;
  background: theme('colors.secondary.800');
  padding: 40px;
  border-radius: 8px;
  border: 1px solid theme('colors.secondary.700');
  box-shadow: 0 0 80px rgba(0, 0, 0, 0.8);
}

.mermaid-zoom-content.dragging {
  transition: none;
}

.mermaid-zoom-content :deep(svg) {
  display: block;
  max-width: none !important;
  max-height: none !important;
}

/* Ensure all SVG elements are visible */
.mermaid-zoom-content :deep(svg *) {
  visibility: visible !important;
}

/* Handle various Mermaid diagram types */
.mermaid-zoom-content :deep(.node),
.mermaid-zoom-content :deep(.edgePath),
.mermaid-zoom-content :deep(.label),
.mermaid-zoom-content :deep(text),
.mermaid-zoom-content :deep(rect),
.mermaid-zoom-content :deep(circle),
.mermaid-zoom-content :deep(path),
.mermaid-zoom-content :deep(line) {
  opacity: 1 !important;
}

/* Transition animations */
.zoom-enter-active,
.zoom-leave-active {
  transition: opacity 0.2s ease;
}

.zoom-enter-from,
.zoom-leave-to {
  opacity: 0;
}
</style>
